import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

import { useFormatting } from '@/composables/useFormatting'
import { useSmartBoost } from '@/components/rateConnect/smartBoost/useSmartBoost'

export type DemandType = 'VERY_LOW' | 'LOW' | 'MEDIUM' | 'HIGH' | 'VERY_HIGH'

export interface BoostOpportunityCard {
  dateRangeStart: string
  dateRangeEnd: string
  opportunityType: string
  boostCpaLevelA: number
  boostCpaLevelB: number
  boostATpShareIncrease: number
  boostBTpShareIncrease: number
  tpShareIncreaseFromZero?: boolean
}

export interface BoostOpportunityChartItem {
  searchedCheckinDate: string
  demandType?: DemandType
  opportunityType: string
  topPositionShareType?: DemandType
}

export interface ShareIncreaseLabel {
  text: string
  colorClass?: string
}

export const DEMAND_LABEL_BY_TYPE: Record<DemandType, string> = {
  VERY_LOW: 'rc_demand_very_low',
  LOW: 'rc_demand_low',
  MEDIUM: 'rc_demand_medium',
  HIGH: 'rc_demand_high',
  VERY_HIGH: 'rc_demand_very_high',
}

export const DEMAND_VALUE_BY_TYPE: Record<DemandType, number> = {
  VERY_LOW: 1,
  LOW: 2,
  MEDIUM: 3,
  HIGH: 4,
  VERY_HIGH: 5,
}

export function findCardForDate(
  cards: BoostOpportunityCard[] | undefined,
  date: string | undefined
): BoostOpportunityCard | null {
  if (!date || !cards?.length) return null

  return cards.find(card => date >= card.dateRangeStart && date <= card.dateRangeEnd) ?? null
}

export function getDemandBarWidth(type: DemandType | undefined): number {
  const value = type ? DEMAND_VALUE_BY_TYPE[type] : undefined
  return value ? (value / 5) * 100 : 0
}

export const SEARCH_DEMAND_BAR_COLORS = {
  default: 'var(--tw-color-blue-500)',
  withOpportunity: 'var(--tw-color-blue-700)',
  defaultHover: 'var(--tw-color-blue-600)',
  withOpportunityHover: 'var(--tw-color-blue-800)',
}

export function hasSearchDemandOpportunity(opportunityType: string | undefined): boolean {
  return opportunityType === 'HOT_OPPORTUNITY' || opportunityType === 'OPPORTUNITY'
}

export function getSearchDemandBarColor(opportunityType: string | undefined): string {
  return hasSearchDemandOpportunity(opportunityType)
    ? SEARCH_DEMAND_BAR_COLORS.withOpportunity
    : SEARCH_DEMAND_BAR_COLORS.default
}

export function getSearchDemandBarHoverColor(opportunityType: string | undefined): string {
  return hasSearchDemandOpportunity(opportunityType)
    ? SEARCH_DEMAND_BAR_COLORS.withOpportunityHover
    : SEARCH_DEMAND_BAR_COLORS.defaultHover
}

export function useBoostOpportunityNavigation() {
  const router = useRouter()
  const { t } = useI18n()
  const { formatPercentage } = useFormatting()
  const { setSmartBoostPrefill } = useSmartBoost()

  const formatShareIncrease = (value: number): string => {
    const maxDigits = Number.isInteger(value) ? 0 : 1
    const formattedValue = formatPercentage(value || 0, 0, maxDigits)

    return value > 0 ? `↑ ${formattedValue}` : formattedValue
  }

  const getShareIncreaseLabel = (
    card: BoostOpportunityCard | undefined,
    shareIncrease: number
  ): ShareIncreaseLabel => {
    if (card?.tpShareIncreaseFromZero) {
      return {
        text: `↑ ${t('rc_top_position_share_increase')}`,
      }
    }

    return {
      text: t('rc_opportunities_top_position_share', {
        PercentageIncrease: formatShareIncrease(shareIncrease),
      }),
      colorClass: 'tw:text-green-700',
    }
  }

  const goToSmartBoost = (card: BoostOpportunityCard | undefined, commissionLevel: number | null | undefined) => {
    if (!card?.dateRangeStart || !card?.dateRangeEnd || commissionLevel == null) return

    setSmartBoostPrefill({
      startDate: card.dateRangeStart,
      endDate: card.dateRangeEnd,
      commission: commissionLevel,
    })

    router.push({
      name: 'rate-connect-running-smart-boost',
    })
  }

  return {
    formatShareIncrease,
    getShareIncreaseLabel,
    goToSmartBoost,
  }
}
